import React, { useState, useMemo } from "react";
import "./agentTable.scss";
import {
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  DataTableSkeleton,
  Pagination,
} from "@carbon/react";
import { useTranslation } from "react-i18next";
import { useGetAgentsList } from "../../../hooks/useGetAgentsList";
import { agentListMapper } from "../../../mappers/agentListMapper";
import CreateFuelRequestModal from "../../createFuelRequestModal/CreateFuelRequestModal";

export default function AgentTable({ farmListData }) {
  const { t, i18n } = useTranslation();
  const [openModal, setOpenModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 5,
  });
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: "", direction: "ASC" });

  const { data: agentListData, isPending: isAgentListPending } =
    useGetAgentsList();

  const { headers: mappedHeaders, rows: mappedRows } = agentListMapper(
    agentListData,
    t,
    setOpenModal,
    setSelectedAgent
  );

  const filteredRows = useMemo(() => {
    if (!search.trim()) return mappedRows;

    return mappedRows.filter((row) =>
      Object.values(row).some((value) => {
        if (React.isValidElement(value)) {
          value = value.props?.children || "";
        }
        return String(value).toLowerCase().includes(search.toLowerCase());
      })
    );
  }, [mappedRows, search]);

  const sortedRows = useMemo(() => {
    if (!sort.key || sort.direction === "NONE") return filteredRows;

    const extractText = (val) => {
      if (val == null) return "";

      if (typeof val === "string" || typeof val === "number") {
        return val.toString();
      }

      if (React.isValidElement(val)) {
        return extractText(val.props?.children);
      }

      if (Array.isArray(val)) {
        return val.map(extractText).join(" ");
      }

      return "";
    };

    const parseValue = (val) => {
      const raw = extractText(val).trim();

      // Проверка: число
      const number = Number(raw);
      if (!isNaN(number) && raw !== "") {
        return { type: "number", value: number };
      }

      // Проверка: дата
      const date = Date.parse(raw);
      if (!isNaN(date)) {
        return { type: "date", value: date };
      }

      return { type: "string", value: raw.toLowerCase() };
    };

    const compare = (aRaw, bRaw) => {
      const a = parseValue(aRaw);
      const b = parseValue(bRaw);

      if (a.type === b.type) {
        if (a.value === b.value) return 0;
        return a.value > b.value ? 1 : -1;
      }

      // Типы разные: приоритет — number > date > string
      const priority = { number: 1, date: 2, string: 3 };
      return priority[a.type] - priority[b.type];
    };

    const sorted = [...filteredRows].sort((a, b) => {
      const aVal = a[sort.key];
      const bVal = b[sort.key];

      const result = compare(aVal, bVal);
      return sort.direction === "ASC" ? result : -result;
    });

    return sorted;
  }, [filteredRows, sort]);

  const paginatedRows = useMemo(() => {
    const start = (pagination.page - 1) * pagination.pageSize;
    const end = start + pagination.pageSize;
    return sortedRows.slice(start, end);
  }, [sortedRows, pagination]);

  return (
    <div className="agent_table">
      {isAgentListPending ? (
        <DataTableSkeleton
          aria-label="sample table"
          headers={[
            {
              header: `${t("admin.agentsList.table.publicName")}`,
              key: "publicName",
            },
            {
              header: `${t("admin.agentsList.table.lastFullReq")}`,
              key: "lastFullReq",
            },
            {
              header: `${t("admin.agentsList.table.action")}`,
              key: "action",
            },
          ]}
          showHeader
          showToolbar
        />
      ) : (
        <DataTable
          size="lg"
          headers={mappedHeaders}
          rows={paginatedRows}
          isSortable
          render={({
            rows,
            headers,
            getHeaderProps,
            getSelectionProps,
            getBatchActionProps,
            onInputChange,
            selectedRows,
          }) => {
            return (
              <TableContainer
                title={t("admin.agentsList.title")}
                description={t("admin.agentsList.description")}
              >
                <TableToolbar>
                  <TableToolbarContent>
                    <TableToolbarSearch
                      value={search}
                      defaultExpanded={!!search}
                      persistent={!!search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPagination((prev) => ({ ...prev, page: 1 }));
                      }}
                      placeholder={`${t("admin.tableText.placeholder")}`}
                      closeButtonLabelText={`${t("admin.tableText.close")}`}
                    />
                  </TableToolbarContent>
                </TableToolbar>
                <Table>
                  <TableHead>
                    <TableRow>
                      {headers.map((header) => {
                        const isSorted = sort.key === header.key;

                        const { key, ...headerProps } = getHeaderProps({
                          header,
                          onClick: () => {
                            let nextDirection;

                            if (sort.key !== header.key) {
                              nextDirection = "ASC";
                            } else if (sort.direction === "ASC") {
                              nextDirection = "DESC";
                            } else if (sort.direction === "DESC") {
                              nextDirection = "NONE";
                            } else {
                              nextDirection = "ASC";
                            }

                            setSort((prev) => ({
                              ...prev,
                              key: header.key,
                              direction: nextDirection,
                            }));
                          },
                        });
                        return (
                          <TableHeader
                            key={key}
                            {...headerProps}
                            isSortHeader={isSorted && sort.direction !== "NONE"}
                            sortDirection={isSorted ? sort.direction : "NONE"}
                          >
                            {t(header.header)}
                          </TableHeader>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.id}>
                        {row.cells.map((cell) => (
                          <TableCell key={cell.id}>{cell.value}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {mappedRows.length && mappedRows.length >= 5 ? (
                  <Pagination
                    backwardText={`${t(
                      "admin.tableText.pagination.backwardText"
                    )}`}
                    forwardText={`${t(
                      "admin.tableText.pagination.forwardText"
                    )}`}
                    itemsPerPageText={`${t(
                      "admin.tableText.pagination.itemsPerPageText"
                    )}`}
                    page={pagination.page}
                    onChange={({ page, pageSize }) => {
                      setPagination((prev) => ({
                        ...prev,
                        page: page,
                        pageSize: pageSize,
                      }));
                    }}
                    pageNumberText={`${t(
                      "admin.tableText.pagination.pageNumberText"
                    )}`}
                    itemRangeText={(min, max, total) =>
                      `${min}–${max} ${t(
                        "admin.tableText.pagination.range"
                      )} ${total} ${
                        total === 1
                          ? t("admin.tableText.pagination.itemRangeText.item")
                          : t("admin.tableText.pagination.itemRangeText.items")
                      }`
                    }
                    pageRangeText={(current, total) =>
                      `${t("admin.tableText.pagination.range")} ${total} ${
                        total === 1
                          ? t("admin.tableText.pagination.pageRangeText.page")
                          : t("admin.tableText.pagination.pageRangeText.pages")
                      }`
                    }
                    pageSize={pagination.pageSize}
                    pageSizes={[5, 10, 15]}
                    size="md"
                    totalItems={filteredRows.length}
                  />
                ) : null}
              </TableContainer>
            );
          }}
        />
      )}

      {openModal && (
        <CreateFuelRequestModal
          openModal={openModal}
          setOpenModal={setOpenModal}
          agent={selectedAgent}
          farmListData={farmListData}
        />
      )}
    </div>
  );
}
